import {useId, useRef, type ReactNode} from 'react';
import styled, {css, keyframes} from 'styled-components';
import {IS_WEBKIT} from '../platform/browserEngine';
import type {PlushState} from './PaiPlush';
import {usePointerGaze, type Gaze} from './usePointerGaze';

/**
 * Shared building blocks for the plush-style mascot concepts: a 200×220
 * frame with fur/fuzz/blur filters, the whole-body state motion, the beady
 * {@link PlushFace}, and the thinking dots and sleepy z's. A concept only
 * draws its own body inside {@link PlushFrame}.
 *
 * Bodies should stand on the ground line near y = 205, which is where the
 * breathing and hopping motion is anchored.
 */

/** SVG viewBox every concept is authored in. */
const VIEWBOX = '0 0 200 220';

/** Height ÷ width of {@link VIEWBOX}. */
export const KIT_ASPECT = 220 / 200;

/** Default face ink — a warm near-black. */
export const KIT_INK = '#2A1E1C';

/** Default blush color. */
export const KIT_CHEEK = '#F4A6AE';

/** Thought dots and z's — a mid tan that reads on light and dark pages. */
const BUBBLE = '#D9C3A0';

/** Largest pupil shift toward the pointer, in face units. */
const GAZE_TRAVEL = 3.5;

/** Where `thinking` looks — up and to the side. */
const THINKING_GAZE: Gaze = {dx: 3, dy: -4};

/**
 * Eye centers in the face's local coordinates. {@link PlushFace} maps the
 * point midway between them, `(100, 104)`, onto the caller's `x`/`y`.
 */
const EYES = [
  {cx: 76, cy: 104},
  {cx: 124, cy: 104},
] as const;

const breathe = keyframes`
  0%, 100% { transform: scale(1, 1); }
  50% { transform: scale(1.02, 0.975); }
`;

const hop = keyframes`
  0%, 55%, 100% { transform: translateY(0) scale(1, 1); }
  12% { transform: translateY(0) scale(1.06, 0.92); }
  30% { transform: translateY(-16px) scale(0.96, 1.05); }
  46% { transform: translateY(0) scale(1.05, 0.94); }
`;

const startle = keyframes`
  0% { transform: translateY(0) scale(1, 1); }
  30% { transform: translateY(-12px) scale(0.95, 1.06); }
  60% { transform: translateY(0) scale(1.04, 0.96); }
  100% { transform: translateY(0) scale(1, 1); }
`;

const tilt = keyframes`
  0%, 100% { transform: rotate(-3deg); }
  50% { transform: rotate(3deg); }
`;

const blink = keyframes`
  0%, 93%, 100% { transform: scaleY(1); }
  96% { transform: scaleY(0.1); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.25; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1); }
`;

const drift = keyframes`
  0% { opacity: 0; transform: translate(0, 0) scale(0.6); }
  25% { opacity: 1; }
  100% { opacity: 0; transform: translate(14px, -34px) scale(1.1); }
`;

const Svg = styled.svg<{$size: number}>`
  display: block;
  width: ${({$size}) => $size}px;
  height: ${({$size}) => $size * KIT_ASPECT}px;
  overflow: visible;

  g,
  circle,
  text {
    transform-box: view-box;
  }

  @media (prefers-reduced-motion: reduce) {
    * {
      animation: none !important;
    }
  }
`;

const Motion = styled.g<{$state: PlushState}>`
  transform-origin: 100px 205px;
  ${({$state}) => {
    switch ($state) {
      case 'happy':
        return css`
          animation: ${hop} 1.4s ease-in-out infinite;
        `;
      case 'surprised':
        return css`
          animation: ${startle} 0.5s ease-out 1;
        `;
      case 'thinking':
        return css`
          animation: ${tilt} 3s ease-in-out infinite;
        `;
      default:
        return css``;
    }
  }}
`;

/**
 * Holds a plush's idle loops (breath, blink, hat sway) still in WebKit.
 * They animate inside the fuzz-filtered SVG, and WebKit re-runs the filter
 * on the CPU every frame: about a third of a core for each idle plush on the
 * page. One-shot and state motions (hop, startle, tilt, thinking, sleep)
 * still play. Put it after the component's own `animation`.
 */
// TODO: [#6329](https://github.com/CodeinPlace/cip/issues/6329) - move body motion to an HTML wrapper and blink by timer, then drop this
export const stillInWebKit = IS_WEBKIT
  ? css`
      animation: none;
    `
  : css``;

const Breath = styled.g<{$isSleepy: boolean}>`
  transform-origin: 100px 205px;
  animation: ${breathe} ${({$isSleepy}) => ($isSleepy ? 4 : 3)}s ease-in-out
    infinite;
  ${stillInWebKit}
`;

/** Blinks its contents; for any open-eyed face, including custom ones. */
export const Blink = styled.g`
  transform-box: fill-box;
  transform-origin: center;
  animation: ${blink} 4.6s ease-in-out infinite;
  ${stillInWebKit}
`;

/** Eases pupils toward a new gaze offset instead of snapping. */
export const GazeShift = styled.g`
  transition: transform 0.18s ease-out;
`;

/** Pulses in and out, staggered by `$delaySec`; for thought dots and glows. */
export const Pulse = styled.circle<{$delaySec: number}>`
  transform-box: fill-box;
  transform-origin: center;
  animation: ${pulse} 1.2s ease-in-out ${({$delaySec}) => $delaySec}s infinite;
`;

const Zee = styled.text<{$delaySec: number}>`
  font-family: 'Jost', system-ui, sans-serif;
  font-weight: 700;
  opacity: 0;
  animation: ${drift} 2.7s ease-out ${({$delaySec}) => $delaySec}s infinite;
`;

/** A fur color ramp: the lit center, the mid tone, and the shaded rim. */
export interface FurStops {
  /** Highlight color at the gradient's lit center. */
  light: string;
  /** Mid-tone color. */
  mid: string;
  /** Shaded color at the rim. */
  dark: string;
}

/** What a concept's body render function receives from {@link PlushFrame}. */
export interface PlushKit {
  /** The expression being shown. */
  state: PlushState;
  /** Pupil offset for open eyes — the pointer gaze, or a fixed upward look while `thinking`. */
  gaze: Gaze;
  /** `url(#…)` of the frame's fur gradient. */
  fur: string;
  /** `url(#…)` of the filter that roughens edges into plush fur. */
  fuzz: string;
  /** `url(#…)` of a soft blur, for cheeks and patches. */
  soft: string;
}

/** Props for {@link PlushFrame}. */
export interface PlushFrameProps {
  /** Rendered width in px (height derives from {@link KIT_ASPECT}). */
  size: number;
  /** Expression to show. */
  state: PlushState;
  /** Colors of the fur gradient exposed as {@link PlushKit.fur}. */
  furStops: FurStops;
  /** Height of the eyes as a fraction of the frame's height, for aiming the gaze. */
  eyeLineFraction: number;
  /**
   * Where the thinking dots start, in viewBox units; they rise up and to the
   * right from here. Omitted skips the dots, for concepts that show thinking
   * their own way.
   */
  thoughtAnchor?: {x: number; y: number};
  /** Where the sleepy z's start, in viewBox units. Omitted reuses `thoughtAnchor`, or skips them if that is also omitted. */
  sleepAnchor?: {x: number; y: number};
  /**
   * Static scenery drawn behind the body and outside its motion, so it
   * stays put while the body hops or tilts. Omitted draws nothing.
   */
  ground?: ReactNode;
  /** Draws the concept's body. */
  children: (kit: PlushKit) => ReactNode;
}

/**
 * The shared frame for a plush concept: SVG, filters, state motion, and the
 * thinking/sleepy overlays. Decorative (`aria-hidden`).
 *
 * @param props - See {@link PlushFrameProps}.
 * @return The mascot SVG.
 * @throws Never.
 */
export function PlushFrame({
  size,
  state,
  furStops,
  eyeLineFraction,
  thoughtAnchor,
  sleepAnchor = thoughtAnchor,
  ground,
  children,
}: PlushFrameProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const pointerGaze = usePointerGaze({
    targetRef: svgRef,
    maxTravel: GAZE_TRAVEL,
    eyeLineFraction,
  });
  // useId output contains colons, which break `url(#...)` references.
  const reactId = useId();
  const idBase = `kit${reactId.replace(/[^a-zA-Z0-9]/g, '')}`;
  const kit: PlushKit = {
    state,
    gaze: state === 'thinking' ? THINKING_GAZE : pointerGaze,
    fur: `url(#${idBase}-fur)`,
    fuzz: `url(#${idBase}-fuzz)`,
    soft: `url(#${idBase}-soft)`,
  };

  return (
    <Svg
      ref={svgRef}
      $size={size}
      viewBox={VIEWBOX}
      aria-hidden
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <radialGradient id={`${idBase}-fur`} cx="42%" cy="32%" r="75%">
          <stop offset="0%" stopColor={furStops.light} />
          <stop offset="60%" stopColor={furStops.mid} />
          <stop offset="100%" stopColor={furStops.dark} />
        </radialGradient>
        {/* User-space region: a bounding-box region ignores stroke width
            and would clip stroked shapes like the plush π's legs. */}
        <filter
          id={`${idBase}-fuzz`}
          filterUnits="userSpaceOnUse"
          x={-40}
          y={-60}
          width={280}
          height={320}
        >
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.9"
            numOctaves={2}
            seed={3}
          />
          <feDisplacementMap in="SourceGraphic" scale={3.5} />
        </filter>
        <filter id={`${idBase}-soft`}>
          <feGaussianBlur stdDeviation={2.2} />
        </filter>
      </defs>

      {ground}
      <Motion $state={state}>
        <Breath $isSleepy={state === 'sleepy'}>{children(kit)}</Breath>
      </Motion>

      {state === 'thinking' &&
        thoughtAnchor !== undefined &&
        [
          {dx: 0, dy: 0, r: 4},
          {dx: 12, dy: -14, r: 5.5},
          {dx: 26, dy: -30, r: 7},
        ].map(({dx, dy, r}, index) => (
          <Pulse
            key={dx}
            cx={thoughtAnchor.x + dx}
            cy={thoughtAnchor.y + dy}
            r={r}
            fill={BUBBLE}
            $delaySec={index * 0.25}
          />
        ))}

      {state === 'sleepy' &&
        sleepAnchor !== undefined &&
        [
          {dx: 0, dy: 0, fontSize: 18},
          {dx: 8, dy: -4, fontSize: 24},
        ].map(({dx, dy, fontSize}, index) => (
          <Zee
            key={dx}
            x={sleepAnchor.x + dx}
            y={sleepAnchor.y + dy}
            fontSize={fontSize}
            fill={BUBBLE}
            $delaySec={index * 1.35}
          >
            z
          </Zee>
        ))}
    </Svg>
  );
}

/** Props for {@link PlushFace}. */
export interface PlushFaceProps {
  /** The frame's kit (state, gaze, filters). */
  kit: PlushKit;
  /** Face center x (midway between the eyes), in viewBox units. */
  x: number;
  /** Eye-line y, in viewBox units. */
  y: number;
  /** Scale of the face. Omitted defaults to `1` (eyes 48 units apart). */
  scale?: number;
  /** Eye and mouth color. Omitted defaults to {@link KIT_INK}. */
  ink?: string;
  /** Blush color. Omitted defaults to {@link KIT_CHEEK}. */
  cheek?: string;
  /**
   * Draws the mouth. Omitted defaults to `true`; pass `false` for faces
   * whose mouth is part of the body, like a beak.
   */
  hasMouth?: boolean;
  /**
   * Color of a ring drawn behind open eyes, for bodies too dark for bare
   * beady eyes to read against. Omitted draws no ring.
   */
  eyeRing?: string;
}

/**
 * The beady-eyed plush face (eyes, mouth, and cheeks) for the kit's state,
 * placed and scaled onto a concept's body.
 *
 * @param props - See {@link PlushFaceProps}.
 * @return The face group.
 * @throws Never.
 */
export function PlushFace({
  kit,
  x,
  y,
  scale = 1,
  ink = KIT_INK,
  cheek = KIT_CHEEK,
  hasMouth = true,
  eyeRing,
}: PlushFaceProps) {
  const {state, gaze, soft} = kit;
  const stroke = {
    stroke: ink,
    strokeWidth: 3.6,
    strokeLinecap: 'round' as const,
    fill: 'none',
  };

  let features: ReactNode;
  if (state === 'happy') {
    features = (
      <>
        {EYES.map(({cx, cy}) => (
          <path
            key={cx}
            d={`M ${cx - 8} ${cy + 3} Q ${cx} ${cy - 8} ${cx + 8} ${cy + 3}`}
            {...stroke}
          />
        ))}
        {hasMouth && (
          <>
            <path
              d="M89 115 Q100 116 111 115 Q109 130 100 130 Q91 130 89 115 Z"
              fill={ink}
            />
            <ellipse cx={100} cy={126} rx={5.5} ry={3} fill="#E97C8A" />
          </>
        )}
      </>
    );
  } else if (state === 'sleepy') {
    features = (
      <>
        {EYES.map(({cx, cy}) => (
          <path
            key={cx}
            d={`M ${cx - 8} ${cy} Q ${cx} ${cy + 7} ${cx + 8} ${cy}`}
            {...stroke}
          />
        ))}
        {hasMouth && <ellipse cx={100} cy={121} rx={3.5} ry={4} fill={ink} />}
      </>
    );
  } else {
    const isSurprised = state === 'surprised';
    features = (
      <>
        {eyeRing !== undefined &&
          EYES.map(({cx, cy}) => (
            <ellipse
              key={cx}
              cx={cx}
              cy={cy}
              rx={isSurprised ? 12 : 10.5}
              ry={isSurprised ? 14 : 12}
              fill={eyeRing}
            />
          ))}
        <Blink>
          <GazeShift
            style={{transform: `translate(${gaze.dx}px, ${gaze.dy}px)`}}
          >
            {EYES.map(({cx, cy}) => (
              <g key={cx}>
                <ellipse
                  cx={cx}
                  cy={cy}
                  rx={isSurprised ? 8 : 6.5}
                  ry={isSurprised ? 10 : 8}
                  fill={ink}
                />
                <circle cx={cx + 2.2} cy={cy - 3.4} r={2.2} fill="#fff" />
              </g>
            ))}
          </GazeShift>
        </Blink>
        {hasMouth && isSurprised && (
          <ellipse cx={100} cy={122} rx={5.5} ry={7} fill={ink} />
        )}
        {hasMouth && state === 'thinking' && (
          <path d="M94 119 Q100 117 106 120" {...stroke} strokeWidth={3.2} />
        )}
        {hasMouth && state === 'idle' && (
          <path d="M91 116 Q100 125 109 116" {...stroke} strokeWidth={3.2} />
        )}
      </>
    );
  }

  return (
    <g transform={`translate(${x} ${y}) scale(${scale}) translate(-100 -104)`}>
      {[62, 138].map(cx => (
        <ellipse
          key={cx}
          cx={cx}
          cy={120}
          rx={12}
          ry={7}
          fill={cheek}
          opacity={state === 'happy' ? 0.95 : 0.75}
          filter={soft}
        />
      ))}
      {features}
    </g>
  );
}

/** Props for {@link KnitBeanie}. */
export interface KnitBeanieProps {
  /** The frame's kit, for its fuzz filter. */
  kit: PlushKit;
  /** Knit color. */
  color: string;
  /** Vertical offset from Beanie's own hat position, in viewBox units. Omitted defaults to `0`. */
  dy?: number;
  /** Size relative to Beanie's hat, scaled about the cuff's bottom center. Omitted defaults to `1`. */
  scale?: number;
}

/**
 * Beanie's ribbed knit hat with a pom-pom, for other concepts to wear. At
 * `dy = 0` and `scale = 1` the cuff spans x 36–164 and sits at y 58–76.
 *
 * @param props - See {@link KnitBeanieProps}.
 * @return The hat group.
 * @throws Never.
 */
export function KnitBeanie({kit, color, dy = 0, scale = 1}: KnitBeanieProps) {
  return (
    <g
      transform={`translate(0 ${dy}) translate(100 76) scale(${scale}) translate(-100 -76)`}
    >
      <g filter={kit.fuzz}>
        <circle cx={100} cy={14} r={13} fill={color} />
        <path
          d="M40 66 C44 32 70 20 100 20 C130 20 156 32 160 66 Z"
          fill={color}
        />
        <rect x={36} y={58} width={128} height={18} rx={9} fill={color} />
      </g>
      <circle cx={100} cy={14} r={13} fill="#fff" opacity={0.18} />
      <rect
        x={36}
        y={58}
        width={128}
        height={18}
        rx={9}
        fill="#fff"
        opacity={0.1}
      />
      <path
        d="M52 62 v10 M64 62 v10 M76 62 v10 M88 62 v10 M100 62 v10 M112 62 v10 M124 62 v10 M136 62 v10 M148 62 v10"
        stroke="#000"
        strokeOpacity={0.25}
        strokeWidth={2}
        strokeLinecap="round"
      />
    </g>
  );
}
